import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import GenericTable, { GenericTableColumn } from "../components/GenericTable";
import PageSectionCard from "../components/PageSectionCard";
import Pagination from "../components/Pagination";
import { useAppSession } from "../context/AppSessionContext";
import { readErrorMessage } from "../lib/http";
import {
  Employee,
  EmployeeLocationType,
  employeeLocationTypeOptions,
  formatEmployeeLocationType,
} from "../types/employees";
import { FollowUpStatus, FollowUpTask } from "../types/incapacity";
import { formatFollowUpType } from "../lib/intakeForm";
import { PaginatedResponse } from "../types/pagination";

type OpenFollowUpStatus = Extract<FollowUpStatus, "pending" | "in_progress">;
type StatusFilter = OpenFollowUpStatus | "open";
type LocationFilter = EmployeeLocationType | "all";
type FollowUpListItem = FollowUpTask & {
  episode?: {
    id: string;
    employee?: Pick<Employee, "fullName" | "locationType" | "workstation">;
  };
};

const defaultPageSize = 20;
const followUpStatuses: FollowUpStatus[] = [
  "pending",
  "in_progress",
  "completed",
  "cancelled",
];
const openFollowUpStatuses: OpenFollowUpStatus[] = ["pending", "in_progress"];

const formatDateTime = (value: string) => {
  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
};

const formatStatusLabel = (status: FollowUpStatus) =>
  status.replaceAll("_", " ");

function FollowUpsPage() {
  const { t } = useTranslation();
  const { authorizedFetch } = useAppSession();

  const [followUps, setFollowUps] = useState<FollowUpListItem[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("open");
  const [locationFilter, setLocationFilter] = useState<LocationFilter>("all");
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [updatingTaskIds, setUpdatingTaskIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const loadFollowUps = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    setUpdateError(null);

    try {
      const query = new URLSearchParams({
        page: String(page),
        limit: String(defaultPageSize),
        status:
          statusFilter === "open"
            ? openFollowUpStatuses.join(",")
            : statusFilter,
      });

      if (locationFilter !== "all") {
        query.set("employeeLocationType", locationFilter);
      }

      const response = await authorizedFetch(`/follow-ups?${query.toString()}`);

      if (!response.ok) {
        throw new Error(
          await readErrorMessage(
            response,
            `Failed to load follow-up tasks (${response.status}).`,
          ),
        );
      }

      const payload = (await response.json()) as PaginatedResponse<FollowUpListItem>;
      const responseTotalPages = payload.totalPages ?? 0;

      if (responseTotalPages > 0 && page > responseTotalPages) {
        setPage(responseTotalPages);
        return;
      }

      setFollowUps(payload.data ?? []);
      setTotal(payload.total ?? 0);
      setTotalPages(responseTotalPages);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to load follow-up tasks.";
      setLoadError(message);
      setFollowUps([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  }, [authorizedFetch, locationFilter, page, statusFilter]);

  useEffect(() => {
    void loadFollowUps();
  }, [loadFollowUps]);

  const onStatusFilterChange = (value: StatusFilter) => {
    setStatusFilter(value);
    setPage(1);
  };

  const onLocationFilterChange = (value: LocationFilter) => {
    setLocationFilter(value);
    setPage(1);
  };

  const onStatusChange = useCallback(
    async (task: FollowUpListItem, status: FollowUpStatus) => {
      if (task.status === status || updatingTaskIds.has(task.id)) {
        return;
      }

    const previousStatus = task.status;

    setUpdateError(null);
    setUpdatingTaskIds((current) => new Set(current).add(task.id));
    setFollowUps((current) =>
      current.map((currentTask) =>
        currentTask.id === task.id
          ? { ...currentTask, status }
          : currentTask,
      ),
    );

    try {
      const response = await authorizedFetch(`/follow-ups/${task.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status }),
      });

      if (!response.ok) {
        throw new Error(
          await readErrorMessage(
            response,
            `Failed to update follow-up task (${response.status}).`,
          ),
        );
      }

      const updated = (await response.json()) as FollowUpListItem;
      setFollowUps((current) =>
        current.map((currentTask) =>
          currentTask.id === task.id ? { ...currentTask, ...updated } : currentTask,
        ),
      );
      toast.success(
        `Updated follow-up status for "${formatFollowUpType(task.type)}".`,
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update follow-up task.";
      setFollowUps((current) =>
        current.map((currentTask) =>
          currentTask.id === task.id
            ? { ...currentTask, status: previousStatus }
            : currentTask,
        ),
      );
      setUpdateError(message);
    } finally {
      setUpdatingTaskIds((current) => {
        const next = new Set(current);
        next.delete(task.id);
        return next;
      });
    }
    },
    [updatingTaskIds, authorizedFetch],
  );

  const columns = useMemo<GenericTableColumn<FollowUpListItem>[]>(
    () => [
      {
        id: "employee",
        header: "Employee",
        cell: (task) => task.episode?.employee?.fullName || "-",
      },
      {
        id: "company",
        header: t("Client company"),
        cell: (task) =>
          task.episode?.employee?.workstation?.clientCompany.name || "-",
      },
      {
        id: "workstation",
        header: t("Workstation"),
        cell: (task) => task.episode?.employee?.workstation?.name || "-",
      },
      {
        id: "type",
        header: "Task",
        cell: (task) => formatFollowUpType(task.type),
      },
      {
        id: "dueDate",
        header: "Due at",
        cell: (task) => formatDateTime(task.dueDate),
      },
      {
        id: "status",
        header: "Status",
        cell: (task) => (
          <select
            className="inputControl tableInlineSelect"
            aria-label={`Status for ${formatFollowUpType(task.type)}`}
            value={task.status}
            onChange={(event) =>
              void onStatusChange(task, event.target.value as FollowUpStatus)
            }
            disabled={updatingTaskIds.has(task.id)}
          >
            {followUpStatuses.map((statusOption) => (
              <option
                key={statusOption}
                value={statusOption}
                disabled={
                  task.status === "completed" &&
                  (statusOption === "pending" || statusOption === "in_progress")
                }
              >
                {formatStatusLabel(statusOption)}
              </option>
            ))}
          </select>
        ),
      },
      {
        id: "episode",
        header: "Episode",
        headerClassName: "actionsColumn",
        cellClassName: "actionsColumn",
        cell: (task) => (
          <Link className="tableActionButton" to={`/episodes/${task.episodeId}`}>
            Open episode
          </Link>
        ),
      },
    ],
    [t, updatingTaskIds, onStatusChange],
  );

  const showEmptyState = !isLoading && !loadError && followUps.length === 0;
  const statusFilterId = "follow-ups-status-filter";
  const locationFilterId = "follow-ups-location-filter";

  return (
    <PageSectionCard
      titleId="follow-ups-title"
      eyebrow="Follow-ups"
      title="Follow-up tasks"
      lead={t("Review open follow-up tasks by due date and update their status.")}
    >
      {updateError ? (
        <p className="errorMessage" role="alert">
          {updateError}
        </p>
      ) : null}

      <div className="listControls" role="group" aria-label="Follow-up filters">
        <label className="field" htmlFor={statusFilterId}>
          <span className="fieldLabel">Status</span>
          <select
            className="inputControl"
            id={statusFilterId}
            value={statusFilter}
            onChange={(event) =>
              onStatusFilterChange(event.target.value as StatusFilter)
            }
          >
            <option value="open">{t("Pending & in progress")}</option>
            {openFollowUpStatuses.map((statusOption) => (
              <option key={statusOption} value={statusOption}>
                {formatStatusLabel(statusOption)}
              </option>
            ))}
          </select>
        </label>

        <label className="field" htmlFor={locationFilterId}>
          <span className="fieldLabel">{t("Location")}</span>
          <select
            className="inputControl"
            id={locationFilterId}
            value={locationFilter}
            onChange={(event) =>
              onLocationFilterChange(event.target.value as LocationFilter)
            }
          >
            <option value="all">All locations</option>
            {employeeLocationTypeOptions.map((locationType) => (
              <option key={locationType} value={locationType}>
                {formatEmployeeLocationType(locationType)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {isLoading ? <p>Loading follow-up tasks...</p> : null}

      {loadError ? (
        <p className="errorMessage" role="alert">
          {loadError}
        </p>
      ) : null}

      {!isLoading && !loadError ? (
        <GenericTable
          ariaLabel="Follow-up tasks"
          columns={columns}
          rows={followUps}
          rowKey={(task) => task.id}
          emptyState={
            showEmptyState ? (
              <p className="hintText">No follow-up tasks found for selected filters.</p>
            ) : null
          }
        />
      ) : null}

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        disabled={isLoading}
        summary={`${total} follow-up task${total === 1 ? "" : "s"}`}
      />
    </PageSectionCard>
  );
}

export default FollowUpsPage;
